import React, { createContext, useContext, useEffect, useState } from 'react';

export interface FeatureFlags {
    dumpsterRental: boolean;
    loadingJobs: boolean;
    aiEstimation: boolean;
    priceAdjustments: boolean;
    tipping: boolean;
    ratings: boolean;
    inAppChat: boolean;
    backgroundCheckRequired: boolean;
    serviceCatalogPricing: boolean;
    estimatesAndQuotes: boolean;
    partialPayments: boolean;
    contractorCertTracking: boolean;
    digitalWorkOrders: boolean;
    inventoryTracking: boolean;
    aiWarrantyLookup: boolean;
    serviceFee: boolean;
    salesTax: boolean;
    serviceAreaMapAndLicenses: boolean;
    estimateBuilder: boolean;
    propertyRecords: boolean;
    complianceDocTracking: boolean;
    photoChecklists: boolean;
    jobSlaTracking: boolean;
    clientPortal: boolean;
    donationCenters: boolean;
    documentShredding: boolean;
}

const defaults: FeatureFlags = {
    dumpsterRental: true,
    loadingJobs: true,
    aiEstimation: true,
    priceAdjustments: true,
    tipping: true,
    ratings: true,
    inAppChat: true,
    backgroundCheckRequired: true,
    // HVAC-driven flags (TREK-131/TREK-133) — default OFF, enabled per-tenant (Fobbus AC) as each piece ships.
    serviceCatalogPricing: false,
    estimatesAndQuotes: false,
    partialPayments: false,
    contractorCertTracking: false,
    digitalWorkOrders: false,
    inventoryTracking: false,
    aiWarrantyLookup: false,
    serviceFee: false,
    salesTax: false,
    serviceAreaMapAndLicenses: false,
    estimateBuilder: false,
    propertyRecords: false,
    complianceDocTracking: false,
    photoChecklists: false,
    jobSlaTracking: false,
    clientPortal: false,
    donationCenters: false,
    documentShredding: false,
};

const FeatureFlagContext = createContext<FeatureFlags>(defaults);

// Separate from FeatureFlagContext on purpose: that context's shape (a plain FeatureFlags object)
// is read directly by 40+ call sites via useFeature/useFeatureFlags, so it keeps its hardcoded
// `defaults` as the value until the real tenant config loads — flipping a flag from its default to
// the real value once fetched is fine for most consumers. But for UI that CONDITIONALLY RENDERS A
// WHOLE ELEMENT based on a flag (e.g. a card in a row of cards), guessing wrong for a moment causes
// a visible flash: the element mounts assuming the default, then unmounts/appears when the real
// value arrives a beat later, reflowing everything next to it. Consumers that render/hide whole
// elements should gate on this instead of rendering optimistically off the default.
const FeatureFlagsReadyContext = createContext<boolean>(false);

export function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
    const [flags, setFlags] = useState<FeatureFlags>(defaults);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const base = process.env.REACT_APP_API_URL || 'http://10.0.0.175:5000';
        fetch(`${base}/api/tenant/config`)
            .then(r => r.ok ? r.json() : null)
            .then((cfg: { features: Partial<FeatureFlags> } | null) => {
                if (cfg?.features) setFlags({ ...defaults, ...cfg.features });
            })
            .catch(() => {})
            .finally(() => setReady(true));
    }, []);

    return (
        <FeatureFlagContext.Provider value={flags}>
            <FeatureFlagsReadyContext.Provider value={ready}>
                {children}
            </FeatureFlagsReadyContext.Provider>
        </FeatureFlagContext.Provider>
    );
}

export function useFeature(flag: keyof FeatureFlags): boolean {
    return useContext(FeatureFlagContext)[flag];
}

export function useFeatureFlags(): FeatureFlags {
    return useContext(FeatureFlagContext);
}

// True once the real tenant config has loaded (or failed to, so we never hang forever) — use this
// to gate whether-to-render-at-all decisions instead of trusting the default value optimistically.
export function useFeatureFlagsReady(): boolean {
    return useContext(FeatureFlagsReadyContext);
}
